import PropTypes from "prop-types";
import { Box, Tooltip, Typography } from "@mui/material";
import {
  CATEGORY,
  FONT,
  WEIGHT,
  INK,
  INK_MUTED,
} from "../../../../theme/tokens";

// A single neatly-aligned resource line: [color dot] LABEL  value.
// The fixed-width label column keeps every value left-aligned so the
// stack reads as a tidy table rather than ragged chips.
const Metric = ({ color, label, value }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
    <Box
      component="span"
      sx={{
        width: 7,
        height: 7,
        borderRadius: "50%",
        bgcolor: color,
        flexShrink: 0,
      }}
    />
    <Typography
      component="span"
      sx={{
        width: 36,
        flexShrink: 0,
        fontSize: FONT.xs,
        fontWeight: WEIGHT.semibold,
        color: INK_MUTED,
        letterSpacing: "0.04em",
      }}
    >
      {label}
    </Typography>
    <Tooltip title={value}>
      <Typography
        component="span"
        sx={{
          fontSize: FONT.xs,
          fontWeight: WEIGHT.medium,
          color: INK,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </Typography>
    </Tooltip>
  </Box>
);
Metric.propTypes = {
  color: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
};

/**
 * Renders pod / CPU / RAM / GPU resources as a tidy, aligned metric list.
 * Accepts normalized prop names so callers don't need to know whether the
 * underlying schema uses `cpu_limit_cores` or `cpu_request_cores`, etc.
 */
export const WorkerResourceChips = ({
  cpuCores,
  memGib,
  gpuCount,
  gpuProduct,
  podCount,
}) => {
  if (cpuCores == null && memGib == null && gpuCount == null) return null;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, py: 0.25 }}>
      {podCount != null && podCount > 0 && (
        <Metric color={CATEGORY.pod} label="PODS" value={String(podCount)} />
      )}
      {cpuCores != null && (
        <Metric color={CATEGORY.cpu} label="CPU" value={`${cpuCores} cores`} />
      )}
      {memGib != null && (
        <Metric color={CATEGORY.ram} label="RAM" value={`${memGib} GiB`} />
      )}
      {gpuCount != null && gpuProduct && (
        <Metric
          color={CATEGORY.gpu}
          label="GPU"
          value={`${gpuCount}× ${gpuProduct}`}
        />
      )}
    </Box>
  );
};
WorkerResourceChips.propTypes = {
  cpuCores: PropTypes.number,
  memGib: PropTypes.number,
  gpuCount: PropTypes.number,
  gpuProduct: PropTypes.string,
  podCount: PropTypes.number,
};
